import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { useFormik } from "formik";
import { Button } from "../../common/Button";
import { failed, success } from "../../common/Toastify";
import { BLOCK_REASON_CODES } from "./constants";
import { createBlock, updateBlock } from "../../api/Scheduling/blocks";
import { listAppointments } from "../../api/Scheduling/appointments";
import { getHold } from "../../api/Scheduling/holds";
import { schedulingErrorMessage, isConflictError } from "../../api/Scheduling/errors";
import { instantRangeToLocal, zonedRangeToInstants } from "./time";
import DateTimeRangeFields from "./DateTimeRangeFields";

const CalendarBlockForm = ({
  show,
  onHide,
  organizationRef,
  schedule,
  block,
  onSaved,
  initialRange,
  timezoneName: displayTimeZone,
}) => {
  const [btnLoading, setBtnLoading] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const isEdit = !!block?.blockId;
  const timezoneName = displayTimeZone || schedule?.timezoneName || "UTC";

  const formik = useFormik({
    initialValues: {
      localDate: "",
      endDate: "",
      startTime: "12:00",
      endTime: "13:00",
      reasonCode: "LUNCH",
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      let interval;
      try { interval = zonedRangeToInstants(values, timezoneName); }
      catch (error) { failed(error.message); return; }
      const { startsAt, endsAt } = interval;
      const body = {
        organizationRef,
        scheduleId: schedule.scheduleId,
        startsAt,
        endsAt,
        reasonCode: values.reasonCode,
      };
      setBtnLoading(true);
      setConflicts([]);
      try {
        if (isEdit) {
          await updateBlock({ ...body, blockId: block.blockId });
        } else {
          await createBlock(body);
        }
        success(isEdit ? "Block updated" : "Block created");
        onSaved?.();
        onHide();
      } catch (error) {
        failed(schedulingErrorMessage(error));
        if (isConflictError(error)) {
          const [appointmentsRes, holdsRes] = await Promise.all([
            listAppointments({ organizationRef, scheduleId: schedule.scheduleId, startsAt, endsAt }).catch(() => ({})),
            getHold({ organizationRef, scheduleId: schedule.scheduleId, startsAt, endsAt, status: "active" }).catch(() => ({})),
          ]);
          const appointments = appointmentsRes?.appointments || (appointmentsRes?.appointment ? [appointmentsRes.appointment] : []);
          const holds = holdsRes?.holds || (holdsRes?.hold ? [holdsRes.hold] : []);
          setConflicts([
            ...appointments.map((item) => `Appointment ${item.status} ${item.startsAt}`),
            ...holds.map((item) => `Hold ${item.status} ${item.startsAt}`),
          ]);
        }
      } finally {
        setBtnLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!show) return;
    setConflicts([]);
    if (block) {
      formik.setValues({
        ...instantRangeToLocal(block.startsAt, block.endsAt, timezoneName),
        reasonCode: block.reasonCode || "LUNCH",
      });
    } else {
      formik.setValues({
        localDate: initialRange?.localDate || "",
        endDate: initialRange?.endDate || initialRange?.localDate || "",
        startInstant: initialRange?.startInstant,
        endInstant: initialRange?.endInstant,
        startTime: initialRange?.startTime || "12:00",
        endTime: initialRange?.endTime || "13:00",
        reasonCode: "LUNCH",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, block, initialRange]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" className="custom-dialog scheduling-dialog">
      <Modal.Header closeButton className="border-0">
        <Modal.Title>{isEdit ? "Edit blocked time" : "Block time"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <DateTimeRangeFields values={formik.values} onChange={formik.setFieldValue} timezoneName={timezoneName} />
          <Row>
            <Col>
              <label>Reason</label>
              <select
                className="form-control"
                value={formik.values.reasonCode}
                onChange={(event) => formik.setFieldValue("reasonCode", event.target.value)}
              >
                {BLOCK_REASON_CODES.map((reason) => (
                  <option key={reason.value} value={reason.value}>
                    {reason.name}
                  </option>
                ))}
              </select>
            </Col>
          </Row>
          {conflicts.length ? (
            <div className="scheduling-panel">
              <h3>Conflicts</h3>
              <ul>
                {conflicts.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
              <p className="mb-0">Move the appointment first. Blocks cannot override booked time.</p>
            </div>
          ) : null}
          <div className="btn-wrap" style={{ display: "flex", marginTop: "1rem" }}>
            <Button variant="secondary" onClick={onHide}>
              Cancel
            </Button>
            <Button type="submit" isLoading={btnLoading}>
              Save
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CalendarBlockForm;
